import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { UseQueryResult } from "@tanstack/react-query";
import {
  ChartBarIcon,
  ClipboardListIcon as ClipboardListIconOutline,
  CodeIcon as CodeIconOutline,
  DocumentTextIcon as DocumentTextIconOutline,
  PencilIcon as PencilSquareIconOutline,
} from "@heroicons/react/outline";
import {
  ClipboardListIcon as ClipboardListIconSolid,
  CodeIcon as CodeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
} from "@heroicons/react/solid";
import { CogIcon, ShareIcon, Stethoscope } from "lucide-react";

import PromptTemplateInputVariablesRow from "../PromptTemplateInputVariablesRow";

import PromptTemplateAnalytics from "./PromptTemplateAnalytics";
import PromptTemplateSnippets from "./PromptTemplateSnippets";
import PromptTemplateVersionSelector from "./PromptTemplateVersionSelector";
import { usePromptOverviewTabFromUrl } from "./hooks/usePromptOverviewTabFromUrl";
import { useXrayMode } from "./hooks/useXrayMode";
import { SharePromptButton } from "./SharePromptButton";

import { Playground } from "@/components/Playground";
import MessagesSettings from "@/components/Playground/Messages/MessagesHeader/MessagesSettings";
import MessagesShimmer from "@/components/Playground/Messages/MessagesShimmer";
import { TEMPLATE_FORMATS } from "@/components/Playground/types";
import ResponsiveActions, { type ResponsiveAction } from "@/components/ui/ResponsiveActions";
import TabNavigation from "@/components/ui/TabNavigation";
import { cn } from "@/lib/utils";
import { PromptBlueprint, PromptVersion } from "@/types";
import { getTemplateFormat } from "@/utils/utils";

interface OverviewContentTabsProps {
  promptVersion: UseQueryResult<PromptVersion>;
  totalVersions: number;
  navigateToPlayground: () => void;
  onEvaluate: () => void;
  isEvaluateDisabled: boolean;
  setShowVersions: (showVersions: boolean) => void;
  showVersions: boolean;
}

const OverviewContentTabs = ({
  promptVersion,
  totalVersions: _totalVersions,
  navigateToPlayground,
  onEvaluate,
  isEvaluateDisabled,
  setShowVersions,
  showVersions,
}: OverviewContentTabsProps) => {
  const { isXrayMode, setIsXrayMode, isXrayAvailable, XrayBlueprint, isXrayLoading } = useXrayMode(
    promptVersion.data,
  );
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  usePromptOverviewTabFromUrl({ onRestoreTabIndex: setSelectedTabIndex });

  const location = useLocation();

  const getCurrentTabIndex = useCallback(() => {
    if (location.pathname.includes("/playground")) {
      return 1;
    }
    if (location.pathname.includes("/analytics")) {
      return 2;
    }
    if (location.pathname.includes("/related")) {
      return 3;
    }
    return 0;
  }, [location.pathname]);

  const isSnippet = Boolean(promptVersion.data?.is_snippet);
  const isContentTab = getCurrentTabIndex() === 0;

  const actions = useMemo(() => {
    const items: ResponsiveAction[] = [];

    if (!isSnippet) {
      items.push({
        id: "share",
        label: "Share",
        icon: <ShareIcon className="h-4 w-4" aria-hidden="true" />,
        onClick: () => setShareOpen(true),
      });
    }

    if (!isSnippet && isContentTab) {
      items.push({
        id: "evaluate",
        label: "Evaluate",
        icon: <ChartBarIcon className="h-4 w-4" aria-hidden="true" />,
        onClick: onEvaluate,
        disabled: isEvaluateDisabled,
        tooltip: "You don't have permission to create an evaluation",
      });
    }

    if (isContentTab) {
      items.push({
        id: "edit",
        label: "Edit",
        icon: <PencilSquareIconOutline className="h-4 w-4" aria-hidden="true" />,
        onClick: navigateToPlayground,
        "aria-label": "Edit in Playground",
        alwaysVisible: true,
      });
    }

    if (isXrayAvailable) {
      items.push({
        id: "xray",
        label: isXrayMode ? "Disable X-Ray Mode" : "Enable X-Ray Mode",
        icon: (
          <Stethoscope className={cn("h-5 w-5", isXrayMode ? "text-blue-600" : "text-gray-500")} />
        ),
        onClick: () => setIsXrayMode(prev => !prev),
        variant: "ghost",
        size: "icon",
        className: "rounded-full transition-all duration-200 active:scale-95",
        tooltip: isXrayMode ? "Disable X-Ray Mode" : "Enable X-Ray Mode",
        showTooltip: true,
      });
    }

    if (promptVersion.data) {
      items.push({
        id: "settings",
        label: "Settings",
        icon: <CogIcon className="h-4 w-4" />,
        onClick: () => setSettingsOpen(true),
        size: "icon",
        variant: "ghost",
        className:
          "h-8 w-8 rounded-full text-gray-500 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 active:scale-95",
      });
    }

    return items;
  }, [
    isSnippet,
    isContentTab,
    isEvaluateDisabled,
    isXrayAvailable,
    isXrayMode,
    navigateToPlayground,
    onEvaluate,
    promptVersion.data,
    setIsXrayMode,
  ]);

  return (
    <div className="flex h-full flex-row gap-x-2">
      <div
        className={cn(
          "min-w-0 shrink-0 overflow-hidden pt-2 transition-[width] duration-300 ease-in-out",
          showVersions ? "w-[360px]" : "w-12",
        )}
      >
        <PromptTemplateVersionSelector
          selectedVersionId={promptVersion.data?.id}
          setShowVersionList={setShowVersions}
          showVersionList={showVersions}
          className="z-20 h-full w-full min-w-0 max-w-none"
        />
      </div>
      <div className="flex h-full min-w-0 flex-1 flex-col overflow-auto">
        {promptVersion.data && !promptVersion.isLoading ? (
          <TabNavigation
            handleTabChange={setSelectedTabIndex}
            selectedIndex={selectedTabIndex}
            actionButtons={
              <div className="relative bottom-1 w-full min-w-0">
                <ResponsiveActions actions={actions} />
                {!isSnippet && (
                  <SharePromptButton hideTrigger open={shareOpen} onOpenChange={setShareOpen} />
                )}
                {promptVersion.data && (
                  <MessagesSettings
                    readonly
                    hideTrigger
                    open={settingsOpen}
                    onOpenChange={setSettingsOpen}
                    formats={TEMPLATE_FORMATS}
                    currentFormat={getTemplateFormat(promptVersion.data.prompt_template)}
                    blueprint={promptVersion.data as PromptBlueprint}
                    metadata={promptVersion.data.metadata ?? undefined}
                    isXRayMode={isXrayMode}
                    onToggleXRay={() => setIsXrayMode(prev => !prev)}
                    hasXRay={isXrayAvailable}
                    isXRayLoading={isXrayLoading}
                    showStreaming={!promptVersion.data.is_snippet}
                    showMetadata={!promptVersion.data.is_snippet}
                    showExport={!promptVersion.data.is_snippet}
                    snippetText={
                      promptVersion.data.is_snippet &&
                      promptVersion.data.prompt_template.type === "completion" &&
                      promptVersion.data.prompt_template.content?.[0]?.type === "text"
                        ? promptVersion.data.prompt_template.content[0].text
                        : undefined
                    }
                  />
                )}
              </div>
            }
            tabs={[
              {
                id: 0,
                name: "Content",
                solidIcon: <DocumentTextIconSolid className="h-4 w-4" />,
                outlineIcon: <DocumentTextIconOutline className="h-4 w-4" />,
                content: (
                  <div className="flex h-full flex-col overflow-hidden">
                    {promptVersion.data.commit_message && (
                      <div className="z-10 bg-white px-3 pb-2 pt-2">
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                          <h3 className="mb-1 text-xs font-medium uppercase text-gray-500">
                            Commit Message
                          </h3>
                          <p className="text-sm text-gray-700">
                            {promptVersion.data.commit_message}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex min-h-0 flex-1 overflow-auto">
                      {isXrayLoading ? (
                        <MessagesShimmer />
                      ) : (
                        <Playground
                          readonly
                          isSnippet={!!promptVersion.data.is_snippet}
                          key={
                            isXrayMode
                              ? `${promptVersion.data.id}_xray_${XrayBlueprint ? "ready" : "off"}`
                              : String(promptVersion.data.id)
                          }
                          selectedTemplateVersion={promptVersion.data}
                          footerLeftContent={
                            <PromptTemplateInputVariablesRow
                              className="w-full max-w-full border-0 bg-transparent px-0 py-0"
                              promptVersion={promptVersion.data}
                              fallbackInputVariables={
                                promptVersion.data.prompt_template.input_variables ||
                                promptVersion.data.rendered_template?.input_variables ||
                                []
                              }
                            />
                          }
                          blueprint={{
                            prompt_template: isXrayMode
                              ? (XrayBlueprint?.prompt_template ??
                                promptVersion.data.prompt_template)
                              : promptVersion.data.prompt_template,
                            provider_base_url_name: promptVersion.data.provider_base_url_name,
                            commit_message: promptVersion.data.commit_message ?? undefined,
                            metadata: promptVersion.data.metadata,
                            provider_id: promptVersion.data.provider_id ?? undefined,
                            report_id: promptVersion.data.report_id ?? undefined,
                          }}
                        />
                      )}
                    </div>
                  </div>
                ),
              },
              ...(!promptVersion.data.is_snippet
                ? [
                    {
                      id: 1,
                      name: "Analytics & Logs",
                      solidIcon: (
                        <ClipboardListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />
                      ),
                      outlineIcon: (
                        <ClipboardListIconOutline className="mr-1.5 h-4 w-4" aria-hidden="true" />
                      ),
                      content: <PromptTemplateAnalytics />,
                    },
                  ]
                : []),
              ...(promptVersion.data?.is_snippet
                ? [
                    {
                      id: 1,
                      name: "Snippet References",
                      solidIcon: <CodeIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
                      outlineIcon: (
                        <CodeIconOutline className="mr-1.5 h-4 w-4" aria-hidden="true" />
                      ),
                      content: <PromptTemplateSnippets />,
                    },
                  ]
                : []),
            ]}
          />
        ) : (
          <div className="h-full">
            <MessagesShimmer />
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewContentTabs;
